/**
 * Utility functions for working with Web Crypto API
 * All functions handle cryptographic operations securely
 */

/**
 * Convert Uint8Array to base64 string
 * Used for storing and transmitting binary data
 */
export function uint8ArrayToBase64(data: Uint8Array): string {
  const binary = String.fromCharCode(...data);
  return btoa(binary);
}

/**
 * Convert base64 string to Uint8Array
 * Used for retrieving and decoding binary data
 */
export function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Convert Uint8Array to base64url string (RFC 4648)
 * URL-safe encoding: no +, /, or = characters
 * Used for keys in URL fragments
 */
export function uint8ArrayToBase64Url(data: Uint8Array): string {
  const base64 = uint8ArrayToBase64(data);
  // Replace + with -, / with _, and remove padding =
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

/**
 * Convert base64url string to Uint8Array (RFC 4648)
 * Decodes URL-safe base64 encoding
 */
export function base64UrlToUint8Array(base64url: string): Uint8Array {
  // Replace - with +, _ with /
  let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  // Add padding if needed
  const padding = base64.length % 4;
  if (padding > 0) {
    base64 += '='.repeat(4 - padding);
  }
  return base64ToUint8Array(base64);
}

/**
 * Convert string to Uint8Array
 * Used for encoding plaintext data
 */
export function stringToUint8Array(str: string): Uint8Array {
  const encoder = new TextEncoder();
  return encoder.encode(str);
}

/**
 * Convert Uint8Array to string
 * Used for decoding plaintext data
 */
export function uint8ArrayToString(data: Uint8Array): string {
  const decoder = new TextDecoder();
  return decoder.decode(data);
}

/**
 * Generate random bytes
 * Cryptographically secure random data for IVs, salts, etc.
 */
export function generateRandomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

/**
 * Generate random UUID
 * Used for secret IDs (128+ bits of entropy)
 */
export function generateRandomId(): string {
  return crypto.randomUUID();
}
